/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { FilePicker } from 'src/core/components/FilePicker';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { FormValidator } from 'src/core/types/form';
import { noop } from 'src/core/utils/base';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type ImportModalProps = React.PropsWithChildren<
    BasicModalProps<ImportModalDto> & {
        /**
         * A flag whether the name is optional or
         * required.
         */
        isNameRequired?: boolean;

        /**
         * Whether or not the optional name field
         * is displayed not.
         */
        isNameFieldDisplayed?: boolean;

        /**
         * An optional label for the
         * picker control.
         */
        fileType?: FileType;

        /**
         * Initial value for the file name to be imported.
         */
        fileName?: string;

        /**
         * Custom name validators for the name field.
         */
        nameValidators?: FormValidator[];

        /**
         * Custom name validation given the  file and the new name.
         */
        validateName?: (file: File, newName: string) => void;
    }
>;

export type ImportModalDto = {
    /**
     * The app file to import.
     */
    file: File;

    /**
     * Optional app name.
     */
    name: string;
};

const messages = defineMessages({
    fileLabel: {
        id: 'apps.importModal.fileLabel',
        defaultMessage: 'Choose a file ({ fileType } format)'
    },
    nameLabel: {
        id: 'apps.importModal.nameLabel',
        defaultMessage: 'Name'
    },
    namePlaceholder: {
        id: 'apps.importModal.namePlaceholder',
        defaultMessage: 'Type the name here...'
    }
});

/**
 * Represents a modal to import a new app.
 *
 * @param props The properties passed to the modal.
 */
export const ImportModal = withModalMounter((props: ImportModalProps) => {
    const {
        onSubmit,
        fileType,
        children,
        fileName = '',
        validateName = noop,
        isNameRequired = false,
        dialogContentProps = {},
        isNameFieldDisplayed = true,
        nameValidators = [FormValidators.createBlacklistValidator()],
        ...rest
    } = props;

    const { localize } = useLocalization();
    if (isNameRequired) {
        nameValidators.push(FormValidators.createRequiredValidator());
    }

    const { formState, formStateDto, updateFormState } = useFormState<ImportModalDto>({
        file: [
            new Blob([], { type: 'file' }) as File,
            FormValidators.createRequiredValidator(),
            FormValidators.createExtensionValidator(fileType)
        ],
        name: [fileName, ...(isNameFieldDisplayed ? nameValidators : [])]
    });

    /**
     * Gets the file picker label based on
     * the modal file type.
     */
    const getFileLabel = () => {
        let format: string;

        switch (fileType) {
            case 'lu':
                format = 'LU';
                break;
            case 'zip':
                format = 'ZIP';
                break;
            case 'tsv':
                format = 'TSV';
                break;
            case 'xlsx':
                format = 'XLSX';
                break;
            case 'json':
            default:
                format = 'JSON';
                break;
        }

        return localize(messages.fileLabel, { fileType: format });
    };

    return (
        <BasicModal
            {...rest}
            valid={formState.form.valid}
            onSubmit={() => onSubmit(formStateDto)}
            dialogContentProps={{ ...dialogContentProps, styles: { inner: { overflowY: 'auto' } } }}
            data-id="Import">
            <Stack verticalFill tokens={{ childrenGap: 20 }}>
                <FilePicker
                    required
                    autoFocus
                    label={getFileLabel()}
                    accept={`.${fileType}`}
                    errorMessage={formState.fields.file.errorMessage}
                    onFilesPicked={(files: FileList) => {
                        updateFormState({ file: files.item(0) });
                        validateName(files.item(0), formStateDto.name);
                    }}
                />

                {isNameFieldDisplayed && (
                    <LuisTextField
                        required={isNameRequired}
                        value={formState.fields.name.value}
                        label={localize(messages.nameLabel)}
                        placeholder={localize(messages.namePlaceholder)}
                        errorMessage={formState.fields.name.errorMessage}
                        onChange={(_, name) => {
                            updateFormState({ name });
                            validateName(formStateDto.file, name);
                        }}
                        onEnterKeyUp={() => formState.form.valid && onSubmit(formStateDto)}
                    />
                )}

                {children}
            </Stack>
        </BasicModal>
    );
});
